<template>
  <div v-if="context" class="tab-payment-receipt-summary">
    <template v-if="hasFullSnapshot">
      <q-card flat bordered class="receipt-panel receipt-panel--tint q-mb-md">
        <q-card-section class="receipt-panel__title text-subtitle2 text-weight-medium q-pb-sm">
          {{ t('sales.tabPaymentReceipt.tabAccountSection') }}
        </q-card-section>
        <q-card-section class="q-pt-none">
          <div class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-5 text-grey-7">{{ t('tabs.tab') }}</div>
            <div class="col">
              <router-link
                v-if="context.tab_id"
                :to="{ name: 'tab-detail', params: { id: context.tab_id } }"
                class="text-primary"
              >
                {{ t('tabs.tabNumber', { id: context.tab_id }) }}
              </router-link>
            </div>
          </div>
          <div v-if="context.owner_label" class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-5 text-grey-7">{{ t('tabs.statement.owner') }}</div>
            <div class="col">{{ context.owner_label }}</div>
          </div>
          <div v-if="isBarTab" class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-5 text-grey-7">{{ t('tabs.statement.barTab') }}</div>
            <div class="col">{{ t('tabs.barTabBadge') }}</div>
          </div>

          <div v-if="showItemList && itemizedEntries.length" class="q-mt-sm q-mb-sm">
            <div class="text-caption text-grey-7 q-mb-xs">
              {{ t('sales.tabPaymentReceipt.itemsChargedToTab') }}
            </div>
            <q-list dense bordered separator class="rounded-borders">
              <q-item v-for="entry in itemizedEntries" :key="entry.id">
                <q-item-section>
                  <q-item-label>{{ entry.description || t('tabs.ledgerDescription') }}</q-item-label>
                  <q-item-label caption>
                    {{ t('sales.tabPaymentReceipt.entryQtyPrice', {
                      qty: formatTabEntryQuantity(entry),
                      price: formatTabEntryUnitPrice(entry)
                    }) }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-item-label>{{ money(entry.amount_minor + (entry.tax_minor || 0)) }}</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </div>

          <div v-if="showTaxBreakdown" class="q-mt-sm">
            <div class="row q-col-gutter-sm q-py-xs receipt-kv">
              <div class="col-6 text-grey-7">{{ t('sales.saleReceiptSubtotal') }}</div>
              <div class="col text-right">{{ money(taxTotals.goodsSubtotalMinor) }}</div>
            </div>
            <div
              v-for="(line, idx) in taxDisplayLines"
              :key="`tab-payment-tax-${idx}`"
              class="row q-col-gutter-sm q-py-xs receipt-kv"
              :class="{ 'q-pl-md': line.isDetail }"
            >
              <div class="col-6 text-grey-7">{{ line.label }}</div>
              <div class="col text-right">{{ money(line.amountMinor) }}</div>
            </div>
          </div>

          <div class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">{{ t('sales.tabPaymentReceipt.tabChargesTotal') }}</div>
            <div class="col text-right">{{ money(context.current_total_minor) }}</div>
          </div>
          <div v-if="(context.credits_minor || 0) > 0" class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">{{ t('tabs.statement.credits') }}</div>
            <div class="col text-right">- {{ money(context.credits_minor) }}</div>
          </div>
          <div v-if="(context.payments_before_minor || 0) > 0" class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">{{ t('sales.tabPaymentReceipt.priorPayments') }}</div>
            <div class="col text-right">- {{ money(context.payments_before_minor) }}</div>
          </div>
          <div class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">{{ t('sales.tabPaymentReceipt.balanceBefore') }}</div>
            <div class="col text-right">{{ money(context.balance_before_minor) }}</div>
          </div>
        </q-card-section>
      </q-card>

      <q-card flat bordered class="receipt-panel receipt-panel--tint q-mb-md">
        <q-card-section class="receipt-panel__title text-subtitle2 text-weight-medium q-pb-sm">
          {{ t('sales.tabPaymentReceipt.thisPaymentSection') }}
        </q-card-section>
        <q-card-section class="q-pt-none">
          <div class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">
              {{ chargeBreakdown.hasChargeBreakdown
                ? t('sales.tabPaymentReceipt.paymentAmount')
                : t('sales.tabPaymentReceipt.amountPaid') }}
            </div>
            <div class="col text-right" :class="{ 'text-h6 text-weight-bold': !chargeBreakdown.hasChargeBreakdown }">
              {{ money(chargeBreakdown.tabPaymentMinor) }}
            </div>
          </div>
          <div v-if="chargeBreakdown.tipMinor > 0" class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">{{ t('sales.saleReceiptTip') }}</div>
            <div class="col text-right">{{ money(chargeBreakdown.tipMinor) }}</div>
          </div>
          <div v-if="chargeBreakdown.feeMinor > 0" class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">{{ t('sales.saleReceiptConvenienceFee') }}</div>
            <div class="col text-right">{{ money(chargeBreakdown.feeMinor) }}</div>
          </div>
          <div v-if="chargeBreakdown.roundingMinor > 0" class="row q-col-gutter-sm q-py-xs receipt-kv">
            <div class="col-6 text-grey-7">{{ t('sales.saleReceiptPennyRounding') }}</div>
            <div class="col text-right">{{ money(chargeBreakdown.roundingMinor) }}</div>
          </div>
          <div
            v-if="chargeBreakdown.hasChargeBreakdown && chargeBreakdown.totalChargedMinor !== chargeBreakdown.tabPaymentMinor"
            class="row q-col-gutter-sm q-py-xs receipt-kv"
          >
            <div class="col-6 text-grey-7 text-weight-medium">{{ t('sales.tabPaymentReceipt.totalCharged') }}</div>
            <div class="col text-right text-h6 text-weight-bold">{{ money(chargeBreakdown.totalChargedMinor) }}</div>
          </div>
        </q-card-section>
        <q-card-section class="q-pt-none">
          <q-card flat bordered class="receipt-panel receipt-panel--emphasis">
            <q-card-section class="text-center">
              <div v-if="(context.balance_after_minor || 0) > 0">
                <div class="text-subtitle2 text-weight-medium">{{ t('sales.tabPaymentReceipt.remainingBalance') }}</div>
                <div class="text-h5 text-weight-bold q-mt-xs">{{ money(context.balance_after_minor) }}</div>
                <div class="text-caption text-grey-7 q-mt-sm">{{ t('tabs.statement.balanceDueHint') }}</div>
              </div>
              <div v-else-if="creditMinor > 0">
                <div class="text-subtitle2 text-weight-medium">{{ t('tabs.statement.creditBalance') }}</div>
                <div class="text-h5 text-weight-bold text-positive q-mt-xs">
                  {{ t('tabs.creditDisplay', { amount: money(creditMinor) }) }}
                </div>
              </div>
              <div v-else>
                <div class="text-subtitle2 text-weight-medium">{{ t('sales.tabPaymentReceipt.paidInFull') }}</div>
                <div class="text-h6 q-mt-xs">{{ money(0) }}</div>
              </div>
            </q-card-section>
          </q-card>
        </q-card-section>
      </q-card>
    </template>

    <q-card v-else flat bordered class="receipt-panel receipt-panel--tint q-mb-md">
      <q-card-section class="receipt-panel__title text-subtitle2 text-weight-medium q-pb-sm">
        {{ t('sales.tabPaymentReceipt.thisPaymentSection') }}
      </q-card-section>
      <q-card-section class="q-pt-none">
        <div class="row q-col-gutter-sm q-py-xs receipt-kv">
          <div class="col-6 text-grey-7">
            {{ chargeBreakdown.hasChargeBreakdown
              ? t('sales.tabPaymentReceipt.paymentAmount')
              : t('sales.tabPaymentReceipt.amountPaid') }}
          </div>
          <div class="col text-right" :class="{ 'text-h6 text-weight-bold': !chargeBreakdown.hasChargeBreakdown }">
            {{ money(chargeBreakdown.tabPaymentMinor || context.payment_amount_minor) }}
          </div>
        </div>
        <div v-if="chargeBreakdown.tipMinor > 0" class="row q-col-gutter-sm q-py-xs receipt-kv">
          <div class="col-6 text-grey-7">{{ t('sales.saleReceiptTip') }}</div>
          <div class="col text-right">{{ money(chargeBreakdown.tipMinor) }}</div>
        </div>
        <div v-if="chargeBreakdown.feeMinor > 0" class="row q-col-gutter-sm q-py-xs receipt-kv">
          <div class="col-6 text-grey-7">{{ t('sales.saleReceiptConvenienceFee') }}</div>
          <div class="col text-right">{{ money(chargeBreakdown.feeMinor) }}</div>
        </div>
        <div
          v-if="chargeBreakdown.hasChargeBreakdown && chargeBreakdown.totalChargedMinor !== chargeBreakdown.tabPaymentMinor"
          class="row q-col-gutter-sm q-py-xs receipt-kv"
        >
          <div class="col-6 text-grey-7 text-weight-medium">{{ t('sales.tabPaymentReceipt.totalCharged') }}</div>
          <div class="col text-right text-h6 text-weight-bold">{{ money(chargeBreakdown.totalChargedMinor) }}</div>
        </div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { tabChargeEntriesForReceipt, formatTabEntryQuantity, formatTabEntryUnitPrice } from '@/utils/tabEntryReceiptItems'
import {
  buildTabPaymentReceiptTaxLines,
  resolveTabPaymentReceiptTaxTotals,
  shouldShowTabPaymentReceiptTaxBreakdown
} from '@/utils/tabPaymentReceiptTax'
import {
  isStructuredTabPaymentContext,
  resolveTabPaymentSaleCharges,
  shouldShowTabPaymentReceiptItems,
  tabPaymentCreditOnTabMinor
} from '@/utils/thermalReceiptVolcoraTabPayment'

const props = defineProps({
  context: { type: Object, default: null },
  sale: { type: Object, default: null },
  currency: { type: String, default: 'USD' }
})

const { t } = useI18n()

const hasFullSnapshot = computed(() => isStructuredTabPaymentContext(props.context))

const isBarTab = computed(() => String(props.context?.tab_purpose || '') === 'bar')

const itemizedEntries = computed(() => tabChargeEntriesForReceipt(props.context?.entries))

const showItemList = computed(() => shouldShowTabPaymentReceiptItems(props.context))

const taxTotals = computed(() => resolveTabPaymentReceiptTaxTotals(props.context))

const showTaxBreakdown = computed(() => shouldShowTabPaymentReceiptTaxBreakdown(props.context))

const taxDisplayLines = computed(() =>
  buildTabPaymentReceiptTaxLines(props.context, { genericTaxLabel: t('sales.saleReceiptTax') })
)

const creditMinor = computed(() => tabPaymentCreditOnTabMinor(props.context))

const chargeBreakdown = computed(() => resolveTabPaymentSaleCharges(props.sale))

function money (minor) {
  const val = (Number(minor) || 0) / 100
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: props.currency || props.context?.currency || 'USD'
  }).format(val)
}
</script>

<style lang="scss" scoped>
.receipt-panel--emphasis {
  border-color: var(--q-primary);
}
</style>
